import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useSEO } from "@/hooks/useSEO";
import { useSettings } from "@/hooks/useSettings";
import { ABOUT_ICONS } from "@/components/icons";
import { fileSrc } from "@/lib/api";

export default function About() {
  useSEO({
    title: "ჩვენ შესახებ — ტაქსოპარკი თბილისი | taxipark.ge",
    description: "taxipark.ge — ავტომობილების გაქირავება ტაქსის მძღოლებისთვის თბილისში: საკუთარი ტექნიკური სერვისი, სრული დაზღვევა და ლიცენზირებული ავტოპარკი.",
    path: "/about",
  });

  const settings = useSettings();
  const about = settings.about || {};
  const cards = about.cards || [];
  const photo = about.photo_url ? fileSrc(about.photo_url) : null;

  return (
    <main data-testid="about-page" className="pt-28 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-semibold mb-3">ჩვენ შესახებ</p>

        {photo ? (
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white" data-testid="about-heading">
                {about.heading}
              </h1>
              <p className="text-lg text-gray-400 mt-6 leading-relaxed" data-testid="about-intro">{about.intro}</p>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="relative"
              data-testid="about-photo-wrap"
            >
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-[#D4AF37]/40 via-transparent to-[#D4AF37]/10" aria-hidden="true" />
              <img
                src={photo}
                alt="taxipark.ge — ჩვენ შესახებ"
                className="relative w-full max-h-[420px] object-cover rounded-2xl border border-white/10"
                data-testid="about-photo"
              />
            </motion.div>
          </div>
        ) : (
          <>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white max-w-3xl" data-testid="about-heading">
              {about.heading}
            </h1>
            <p className="text-lg text-gray-400 mt-6 max-w-3xl leading-relaxed" data-testid="about-intro">{about.intro}</p>
          </>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-16">
          {cards.map((v, i) => {
            const Icon = ABOUT_ICONS[v.icon] || ABOUT_ICONS.star;
            return (
              <div key={i} data-testid={`about-value-${i}`} className="card-lux p-8">
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/12 flex items-center justify-center mb-5">
                  <Icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h2 className="text-xl font-semibold text-white">{v.title}</h2>
                <p className="text-sm text-gray-400 mt-3 leading-relaxed">{v.text}</p>
              </div>
            );
          })}
        </div>

        <div className="card-lux p-10 md:p-14 mt-16 text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-white" data-testid="about-cta-title">{about.cta_title}</h2>
          <p className="text-gray-400 mt-4 max-w-xl mx-auto leading-relaxed">{about.cta_text}</p>
          <Link to="/tavisufali-manqanebi" data-testid="about-catalog-cta" className="btn-gold mt-8">
            ნახე ავტომობილები <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </main>
  );
}
